import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { X, ChevronDown } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { isActive as checkIsActive } from '../utils'
import { IMAGE_URLS } from 'src/assets/urlLinks'

import { Separator } from 'src/shared/components/ui/separator'
import { Button, Image } from 'src/shared/components/ui'
import { isNavItemActive } from '../Sidebar/utils'

interface NavDropdownItem {
  label: string
  href: string
}

interface MobileNavItem {
  label?: string
  href: string
  icon?: React.ReactNode
  subItems?: { label: string; href: string }[]
  expandWhenPathPrefix?: string
  dropdown?: {
    columns: {
      title: string
      items: NavDropdownItem[]
    }[]
  }
}

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  navItems: MobileNavItem[]
  onItemClick?: (href: string) => void
  loggedIn?: boolean
  onLogin?: () => void
  onSignup?: () => void
}

const navItemClass = (active: boolean) =>
  cn(
    'w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-left text-sm font-medium transition-colors',
    active
      ? 'bg-gray-100 text-neutral-800'
      : 'text-neutral-600 hover:bg-gray-100 hover:text-neutral-800'
  )

export const MobileMenu = ({
  isOpen,
  onClose,
  navItems,
  onItemClick,
  loggedIn,
  onLogin,
  onSignup,
}: MobileMenuProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLocalization()
  const [expandedDropdown, setExpandedDropdown] = useState<number | null>(null)

  const isOnMyAccountPage =
    location.pathname.startsWith(paths.myAccount) ||
    location.pathname.startsWith(paths.admin)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const idx = navItems.findIndex((item) => {
      if (item.href === 'divider') return false
      const hasSub = item.subItems && item.subItems.length > 0
      if (!hasSub) return false
      if (
        item.expandWhenPathPrefix &&
        location.pathname.startsWith(item.expandWhenPathPrefix)
      ) {
        return true
      }
      return item.subItems!.some((s) =>
        isNavItemActive(s.href, location.pathname)
      )
    })
    setExpandedDropdown(idx >= 0 ? idx : null)
  }, [isOpen, location.pathname, navItems])

  const handleNavigation = (href: string) => {
    const handler =
      onItemClick ??
      ((h: string) => {
        navigate(h)
        onClose()
      })
    handler(href)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/[0.05] lg:hidden"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-[101] flex w-[85%] max-w-[320px] flex-col rounded-l-xl border-l bg-background shadow-lg lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={t('Menu', 'Meny')}
          >
            <Column className="h-full">
              <Row className="items-center justify-between border-b border-border px-4 py-3 pr-3">
                <Image
                  src={IMAGE_URLS.logos.klayder}
                  alt="Klayder"
                  className="h-7 object-cover cursor-pointer"
                  onClick={() => handleNavigation(paths.home)}
                />
                <button
                  type="button"
                  onClick={onClose}
                  className="flex size-10 items-center justify-center rounded-full bg-white/80 opacity-90 shadow-sm transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label={t('Close menu', 'Stäng menyn')}
                >
                  <X className="h-5 w-5" />
                </button>
              </Row>

              <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-hide">
                <Column className="gap-1">
                  {loggedIn && !isOnMyAccountPage && (
                    <button
                      type="button"
                      onClick={() =>
                        handleNavigation(paths.myAccount + paths.dashboard)
                      }
                      className={navItemClass(false)}
                    >
                      {t('My pages', 'Mina sidor')}
                    </button>
                  )}

                  {navItems.map((item, idx) => {
                    if (item.href === 'divider') {
                      return (
                        <Separator
                          key={`mobile-nav-divider-${idx}`}
                          className="my-2"
                        />
                      )
                    }

                    const hasSubItems =
                      Array.isArray(item.subItems) && item.subItems.length > 0
                    const hasDropdown = !!item.dropdown
                    const isExpanded = expandedDropdown === idx

                    const subActive =
                      hasSubItems &&
                      item.subItems!.some((s) =>
                        isNavItemActive(s.href, location.pathname)
                      )
                    const prefixActive =
                      item.expandWhenPathPrefix != null &&
                      location.pathname.startsWith(item.expandWhenPathPrefix)
                    const active =
                      subActive ||
                      prefixActive ||
                      (!hasSubItems &&
                        !hasDropdown &&
                        checkIsActive(location.pathname, item.href))

                    return (
                      <div key={`mobile-nav-${idx}`}>
                        <button
                          type="button"
                          onClick={() => {
                            if (hasSubItems || hasDropdown) {
                              setExpandedDropdown(isExpanded ? null : idx)
                            } else {
                              handleNavigation(item.href)
                            }
                          }}
                          className={navItemClass(active)}
                        >
                          {item.icon && (
                            <span className="text-muted-foreground">
                              {item.icon}
                            </span>
                          )}
                          <span className="flex-1">{item.label}</span>
                          {(hasSubItems || hasDropdown) && (
                            <ChevronDown
                              className={cn(
                                'h-4 w-4 text-muted-foreground transition-transform',
                                isExpanded && 'rotate-180'
                              )}
                            />
                          )}
                        </button>

                        <AnimatePresence>
                          {hasSubItems && isExpanded && item.subItems && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <Column className="gap-1 py-1 pl-3">
                                {item.subItems.map((subItem, subIdx) => {
                                  const subItemActive = isNavItemActive(
                                    subItem.href,
                                    location.pathname
                                  )
                                  return (
                                    <button
                                      key={subIdx}
                                      type="button"
                                      onClick={() =>
                                        handleNavigation(subItem.href)
                                      }
                                      className={navItemClass(subItemActive)}
                                    >
                                      {subItem.label}
                                    </button>
                                  )
                                })}
                              </Column>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <AnimatePresence>
                          {hasDropdown && isExpanded && item.dropdown && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <Column className="gap-3 py-2 pl-3">
                                {item.dropdown.columns.map(
                                  (column, colIdx) => (
                                    <Column key={colIdx} className="gap-1">
                                      <p className="px-4 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                        {column.title}
                                      </p>
                                      {column.items.map(
                                        (dropdownItem, itemIdx) => {
                                          const hasLink =
                                            dropdownItem.href &&
                                            dropdownItem.href !== ''
                                          return (
                                            <button
                                              key={itemIdx}
                                              type="button"
                                              onClick={() =>
                                                hasLink &&
                                                handleNavigation(
                                                  dropdownItem.href
                                                )
                                              }
                                              className={cn(
                                                navItemClass(false),
                                                !hasLink &&
                                                  'cursor-default text-muted-foreground hover:bg-transparent hover:text-muted-foreground'
                                              )}
                                            >
                                              {dropdownItem.label}
                                            </button>
                                          )
                                        }
                                      )}
                                    </Column>
                                  )
                                )}
                              </Column>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )
                  })}
                </Column>
              </nav>

              <div className="border-t border-border p-4">
                {!loggedIn && (onLogin || onSignup) && (
                  <Column className="mb-4 gap-2">
                    {onSignup && (
                      <Button
                        variant="primaryOrange"
                        className="w-full"
                        onClick={() => {
                          onSignup()
                          onClose()
                        }}
                      >
                        {t('Sign up', 'Skapa konto')}
                      </Button>
                    )}
                    {onLogin && (
                      <Button
                        variant="ghost"
                        className="w-full"
                        onClick={() => {
                          onLogin()
                          onClose()
                        }}
                      >
                        {t('Log in', 'Logga in')}
                      </Button>
                    )}
                  </Column>
                )}
                <p className="text-center text-sm text-neutral-400">
                  © {new Date().getFullYear()} Klayder
                </p>
              </div>
            </Column>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
